<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const columnTable = [
  { kolom: 'Bahan Baku', penjelasan: 'Nama bahan baku beserta kode SKU-nya (jika ada)', contoh: 'Kopi Arabika Gayo · KOP-ARA-001' },
  { kolom: 'Kategori', penjelasan: 'Kategori bahan baku', contoh: 'Bahan Utama' },
  { kolom: 'Stok Saat Ini', penjelasan: 'Jumlah stok yang tersisa sekarang, warna merah jika di bawah minimum', contoh: '3.500 gram' },
  { kolom: 'Min Stok', penjelasan: 'Batas minimum yang Anda atur sebagai alert', contoh: '500 gram' },
  { kolom: 'Avg Cost', penjelasan: 'Rata-rata harga beli per satuan dari histori pembelian', contoh: 'Rp 150' },
  { kolom: 'Nilai Stok', penjelasan: 'Stok Saat Ini dikali Avg Cost — perkiraan nilai uang stok Anda', contoh: 'Rp 525.000' },
  { kolom: 'Masuk', penjelasan: 'Total bahan baku yang masuk (dibeli/diterima) selama periode filter', contoh: '+2.000 gram' },
  { kolom: 'Keluar', penjelasan: 'Total bahan baku yang keluar (terpakai untuk produksi) selama periode filter', contoh: '-1.800 gram' },
  { kolom: 'Status', penjelasan: 'Chip "Aman" (hijau) atau "Kritis" (merah) berdasarkan perbandingan Stok Saat Ini vs Min Stok', contoh: 'Kritis' },
]

const faq = [
  {
    q: 'Kolom "Masuk" dan "Keluar" kosong semua, kenapa?',
    a: 'Kolom ini hanya terisi jika Anda mengisi filter "Dari Tanggal" dan "Sampai Tanggal". Jika filter tanggal dikosongkan, sistem hanya menampilkan kondisi stok saat ini tanpa riwayat pergerakan periode tertentu.',
  },
  {
    q: 'Apa itu "Avg Cost" dan kenapa bisa berubah-ubah?',
    a: 'Avg Cost adalah rata-rata harga beli bahan baku dari beberapa kali pembelian. Jika Anda beli kopi dengan harga berbeda dari supplier berbeda, sistem akan menghitung rata-ratanya secara otomatis. Ini normal dan mencerminkan kondisi harga pasar.',
  },
  {
    q: 'Bagaimana cara cepat melihat bahan baku yang perlu direstok?',
    a: 'Aktifkan switch "Tampilkan stok kritis saja" di atas tabel. Tabel akan otomatis menyaring hanya bahan baku dengan status "Kritis".',
  },
  {
    q: 'Apa gunanya kolom "Nilai Stok"?',
    a: 'Nilai Stok membantu Anda tahu berapa uang yang "tertanam" dalam bentuk bahan baku di gudang. Total dari semua baris ditampilkan di kartu "Total Nilai Stok" di bagian atas halaman.',
  },
  {
    q: 'Saya cari nama bahan tapi tidak ketemu di tabel, kenapa?',
    a: 'Cek dulu apakah switch "Tampilkan stok kritis saja" sedang aktif — jika bahan yang Anda cari statusnya "Aman", bahan itu akan tersembunyi. Matikan switch tersebut lalu cari lagi.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink to="/userguide/laporan" class="text-decoration-none text-medium-emphasis text-body-2">
            Laporan
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Laporan Inventory</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="info" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-archive-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">Laporan Inventory</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Status stok dan pergerakan bahan baku
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong> Membantu Anda memantau berapa
          banyak stok bahan baku yang tersisa, berapa nilai uangnya, dan bahan
          apa saja yang sudah waktunya dibeli lagi.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="info" />
            Apa itu Laporan Inventory?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Anggap Laporan Inventory seperti "kartu stok" toko kelontong zaman
              dulu — tapi otomatis dan real-time. Di sini Anda bisa lihat semua
              bahan baku, berapa sisanya, berapa nilainya dalam Rupiah, dan mana
              yang sudah menipis.
            </p>
            <p class="text-body-2 mb-0">
              Laporan ini juga bisa menampilkan pergerakan stok (barang masuk dan
              keluar) jika Anda mengisi filter tanggal — berguna untuk cek
              pemakaian bahan dalam periode tertentu.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Penggunaan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-cursor-line" class="me-2" color="success" />
            Cara Menggunakan
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-3">
                Buka menu <strong>Laporan → Inventory</strong> dari sidebar kiri
              </li>
              <li class="mb-3">
                (Opsional) Isi <strong>"Dari Tanggal"</strong> dan <strong>"Sampai Tanggal"</strong>
                jika ingin melihat pergerakan stok pada periode tertentu.
                Jika dikosongkan, laporan hanya menampilkan kondisi stok terkini
              </li>
              <li class="mb-3">
                Klik <strong>"Refresh"</strong> untuk memuat data terbaru
              </li>
              <li class="mb-3">
                Gunakan kotak <strong>"Cari bahan baku..."</strong> untuk mencari
                nama, SKU, atau kategori tertentu
              </li>
              <li class="mb-3">
                Aktifkan switch <strong>"Tampilkan stok kritis saja"</strong> jika
                hanya ingin melihat bahan yang perlu segera direstok
              </li>
              <li class="mb-0">
                Klik tombol <strong>"Excel"</strong> atau <strong>"PDF"</strong> untuk
                menyimpan laporan
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Summary Cards -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-numbers-line" class="me-2" color="primary" />
            Kartu Ringkasan
          </VCardTitle>
          <VCardText>
            <ul class="text-body-2 ps-4 mb-0">
              <li class="mb-2">
                <strong>Total Nilai Stok</strong> — total perkiraan nilai uang dari
                semua bahan baku yang ada di gudang saat ini
              </li>
              <li class="mb-0">
                <strong>Stok Kritis</strong> — jumlah item bahan baku yang stoknya
                sudah di bawah batas minimum
              </li>
            </ul>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tabel Kolom -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-table-line" class="me-2" color="info" />
            Penjelasan Kolom Tabel
          </VCardTitle>
          <VCardText>
            <VTable>
              <thead>
                <tr>
                  <th style="min-width: 130px">Kolom</th>
                  <th>Penjelasan</th>
                  <th style="min-width: 140px">Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in columnTable" :key="row.kolom">
                  <td><strong>{{ row.kolom }}</strong></td>
                  <td class="text-body-2 py-2">{{ row.penjelasan }}</td>
                  <td class="text-body-2 py-2">{{ row.contoh }}</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Catatan Penting -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VAlert type="warning" variant="tonal" icon="ri-alert-line">
          <strong>Catatan Penting:</strong> Angka "Masuk" dan "Keluar" hanya
          menghitung pergerakan stok dalam rentang tanggal filter. Jika Anda
          tidak mengisi tanggal, kedua kolom ini mungkin tidak menampilkan data
          yang relevan. Untuk analisa pemakaian bahan bulanan, selalu isi filter tanggal.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Cek laporan ini seminggu sekali</strong> untuk merencanakan
                  pembelian bahan baku, bukan menunggu sampai stok benar-benar habis.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Bandingkan "Nilai Stok" dari waktu ke waktu.</strong>
                  Jika nilainya terus naik tapi penjualan tidak naik, mungkin ada
                  bahan baku yang menumpuk dan berisiko kadaluarsa.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Export ke Excel setiap akhir bulan</strong> sebagai arsip,
                  supaya Anda punya catatan riwayat stok untuk perbandingan tahunan.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Halaman Terkait</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Dashboard', sub: 'Ringkasan stok kritis di halaman utama', icon: 'ri-dashboard-line', color: 'primary', to: '/userguide/laporan/dashboard' },
              { title: 'Bahan Baku', sub: 'Kelola data bahan baku', icon: 'ri-flask-line', color: 'success', to: '/userguide/produk-inventory/bahan-baku' },
              { title: 'Stock Opname', sub: 'Cocokkan stok fisik dengan sistem', icon: 'ri-clipboard-line', color: 'warning', to: '/userguide/produk-inventory/stock-opname' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
